import { Injectable } from '@angular/core';

/**
 * StorageAvailabilityService
 *
 * Probes whether the browser will let the app persist a session. On managed/locked-down
 * school devices an admin may disable localStorage and/or cookies, which means the session
 * cannot survive a page reload. The UX nudge uses these probes to tell the user what to fix.
 *
 * The checks are best-effort and side-effect free: each probe writes a throwaway key/cookie
 * and removes it again, swallowing any error thrown by a blocked storage backend.
 */
@Injectable({ providedIn: 'root' })
export class StorageAvailabilityService {
  private readonly PROBE_KEY = '__loqt_probe__';

  /** True when localStorage can be written, read back, and cleared. */
  isLocalStorageWritable(): boolean {
    try {
      localStorage.setItem(this.PROBE_KEY, '1');
      const ok = localStorage.getItem(this.PROBE_KEY) === '1';
      localStorage.removeItem(this.PROBE_KEY);
      return ok;
    } catch {
      return false;
    }
  }

  /** True when cookies are enabled and a test cookie round-trips. */
  areCookiesWritable(): boolean {
    try {
      if (!navigator.cookieEnabled) {
        return false;
      }
      document.cookie = `${this.PROBE_KEY}=1; path=/; SameSite=Lax`;
      const ok = document.cookie.split('; ').some((c) => c.startsWith(`${this.PROBE_KEY}=`));
      // Expire the probe cookie.
      document.cookie = `${this.PROBE_KEY}=; path=/; Max-Age=0; SameSite=Lax`;
      return ok;
    } catch {
      return false;
    }
  }

  /** True when both localStorage and cookies are writable. */
  isPersistenceAvailable(): boolean {
    return this.isLocalStorageWritable() && this.areCookiesWritable();
  }
}
